// =============================================================================
// clientComms.ts — Patch 013.B frontend API client.
//
// Mirrors workers/core-os/src/routes/client_comms.ts. Request/auth behavior
// stays centralized in request.ts.
// =============================================================================

import { request } from './request'
import type {
  ClientComm,
  ClientCommListFilters,
  ClientCommScanResult,
  ClientCommUpdatePatch,
  ProjectCommPref,
} from './types'

function query(filters?: ClientCommListFilters): string {
  if (!filters) return ''
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.project_id) params.set('project_id', filters.project_id)
  if (filters.profile_id) params.set('profile_id', filters.profile_id)
  if (filters.comm_type) params.set('comm_type', filters.comm_type)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function listClientComms(filters?: ClientCommListFilters): Promise<{
  ok: true
  communications: ClientComm[]
  count: number
}> {
  return request(`/api/client-comms${query(filters)}`)
}

export function getClientComm(id: string): Promise<{
  ok: true
  communication: ClientComm
}> {
  return request(`/api/client-comms/${encodeURIComponent(id)}`)
}

export function updateClientComm(
  id: string,
  patch: ClientCommUpdatePatch,
): Promise<{
  ok: true
  communication: ClientComm
  request_id: string
}> {
  return request(`/api/client-comms/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function archiveClientComm(id: string): Promise<{
  ok: true
  communication?: ClientComm
  archived_at?: string
  request_id?: string
}> {
  return request(`/api/client-comms/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function sendClientCommNow(id: string): Promise<{
  ok: true
  communication: ClientComm
  request_id: string
}> {
  return request(`/api/client-comms/${encodeURIComponent(id)}/send`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export function scanClientComms(): Promise<{ ok: true } & ClientCommScanResult> {
  return request('/api/client-comms/scan', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export function listProjectCommPrefs(projectId: string): Promise<{
  ok: true
  prefs: ProjectCommPref[]
  count: number
}> {
  return request(`/api/projects/${encodeURIComponent(projectId)}/comm-prefs`)
}

export function updateProjectCommPrefs(
  projectId: string,
  prefs: Record<string, unknown>,
): Promise<{
  ok: true
  pref_ids: string[]
  request_id: string
}> {
  return request(`/api/projects/${encodeURIComponent(projectId)}/comm-prefs`, {
    method: 'PUT',
    body: JSON.stringify(prefs),
  })
}

export const clientCommsApi = {
  list: listClientComms,
  get: getClientComm,
  update: updateClientComm,
  archive: archiveClientComm,
  sendNow: sendClientCommNow,
  scan: scanClientComms,
  listPrefs: listProjectCommPrefs,
  updatePrefs: updateProjectCommPrefs,
}
